import { z } from "zod";

/**
 * What the shopper's app can do with an open chat beyond its look (since SDK 2.2.0): send a
 * message, start over, open the basket, set the suggestions and the language, add restrictions,
 * and follow what the chat is doing. All additive to envelope v1, announced in
 * `ready.capabilities` (CONTROL_CAPABILITIES).
 */

/**
 * Kinds of product a retailer, or its app, never shows (agent settings `restrictions`). An app
 * can add to the retailer's list, never take from it.
 */
export const RESTRICTABLE_KINDS = [
	"alcohol",
	"alcohol_free",
	"pork",
	"tobacco",
] as const;
export type RestrictableKind = (typeof RESTRICTABLE_KINDS)[number];

/** A suggestion on the welcome screen: what it says, and what tapping it sends. */
export const chatStarterSchema = z.object({
	label: z.string().trim().min(1).max(60),
	/** Sent as the shopper's message; the label when absent. */
	prompt: z.string().trim().min(1).max(300).optional(),
});
export type ChatStarter = z.infer<typeof chatStarterSchema>;

/** The app's suggestions, replacing the retailer's; an empty list brings the retailer's back. */
export const chatStartersSchema = z.object({
	items: z.array(chatStarterSchema).max(6),
});

/** What the chat is doing, posted whenever it changes, so the app's own header can follow it. */
export const chatStateSchema = z.object({
	/** The first screen, or a conversation with at least one message. */
	view: z.enum(["welcome", "conversation"]),
	/** A reply is on its way. */
	busy: z.boolean(),
	basketOpen: z.boolean(),
	/** Units in the basket. */
	itemCount: z.number().int().nonnegative(),
	conversationId: z.string().optional(),
});
export type ChatState = z.infer<typeof chatStateSchema>;

/** The host-to-page control messages' capability names, listed in `ready` when supported. */
export const CONTROL_CAPABILITIES = [
	"chat.send",
	"chat.new",
	"basket",
	"starters",
	"locale",
	"restrictions",
	"state",
] as const;
